import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Ticket, Users, Check, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCustomers, setRecentCustomers] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [vRes, cRes] = await Promise.all([
          axios.get(`${API}/vouchers/stats`),
          axios.get(`${API}/customers?limit=5`),
        ]);
        setStats({
          totalVouchers: vRes.data.total,
          assigned: vRes.data.assigned,
          redeemed: vRes.data.redeemed,
          available: vRes.data.available,
          totalCustomers: cRes.data.total,
        });
        setRecentCustomers(cRes.data.customers.slice(0, 5));
      } catch {}
    };
    load();
  }, []);

  const brandName = user?.brand_name || "Brand";

  return (
    <div className="space-y-6" data-testid="brand-dashboard">
      <div>
        <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">
          Welcome, {brandName}
        </h2>
        <p className="text-[#64748B] text-sm mt-1">Your loyalty program overview</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-[#E2E8F0]" data-testid="brand-stat-customers">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <Users size={22} className="text-[#002FA7]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Customers</p>
                <p className="font-heading text-2xl font-bold">{stats.totalCustomers}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0]" data-testid="brand-stat-vouchers">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <Ticket size={22} className="text-[#92400E]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Total Vouchers</p>
                <p className="font-heading text-2xl font-bold">{stats.totalVouchers}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0]" data-testid="brand-stat-assigned">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <Clock size={22} className="text-[#166534]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Assigned</p>
                <p className="font-heading text-2xl font-bold text-[#002FA7]">{stats.assigned}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-[#E2E8F0]" data-testid="brand-stat-redeemed">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                <Check size={22} className="text-[#6B21A8]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-[#64748B]">Redeemed</p>
                <p className="font-heading text-2xl font-bold text-[#8B5CF6]">{stats.redeemed}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {recentCustomers.length > 0 && (
        <Card className="border border-[#E2E8F0]">
          <CardContent className="p-5">
            <h3 className="font-heading text-base font-semibold mb-4">Recent Customers</h3>
            <div className="divide-y divide-[#E2E8F0]">
              {recentCustomers.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-3" data-testid={`brand-recent-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium">{c.name || "Unknown"}</p>
                    <p className="text-xs text-[#64748B] font-mono">{c.phone}</p>
                  </div>
                  {c.voucher_code && (
                    <span className="text-xs font-mono bg-[#F0FDF4] text-[#166534] px-2 py-1 rounded">
                      {c.voucher_code}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
